import {
  SimpleShowLayout,
  Show,
  TextField,
  DateField,
  ReferenceField,
  Datagrid,
  ArrayField,
  ReferenceArrayField,
  SingleFieldList,
  FunctionField,
  useShowController,
  Labeled,
  Button,
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  SimpleForm,
  useNotify,
  useRefresh,
  useSimpleFormIteratorItem,
} from "react-admin";
import { Accordion, AccordionDetails, AccordionSummary, Box, Grid, Typography, useMediaQuery } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { PreviewExamProtocolButton, CampaignExamButtons, ExamStatusSymbols } from "../Show/";
import { useEffect, useState } from "react";
import { settings } from "app";
import { CampaignSubTypes } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import CitizenshipFiled from "../components/Fileds/CitizenshipFiled";
import CampaignTypeField from "../components/Fileds/CampaignTypeField";
import DirectionField from "../components/Fileds/DirectionField";
import NameBgField from "../components/Fileds/NameBgField";
import NameEnField from "../components/Fileds/NameEnField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RoomField from "server/components/Fields/RoomField";
import BackButtonToolbar from "features/BackButtonToolbar";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import FinalizeCampaignButton from "../components/FinalizeCampaignButton";
import GoogleUsersSection from "../GoogleUsers/GoogleUsersSection";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import dataProvider from "server/dataProvider";
import { documentRequests } from "api/documentsApi";
import { paymentRequests } from "api/paymentApi";
import { useNotifyError } from "utils/useNotifyError";
import { SectionTitle } from "features/SectionTitle";

const IndexField = () => {
  const { index } = useSimpleFormIteratorItem();
  return (
    <TextField
      readOnly
      source=""
      defaultValue={`Приоритет ${index + 1} `}
    />
  );
};

const ShowCampaign = () => {
  const notifyError = useNotifyError();
  const notify = useNotify();
  const refresh = useRefresh();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const { record } = useShowController();
  const [anyExamPassed, setAnyExamPassed] = useState(false);

  const [addMode, setAddMode] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const isEditMode = addMode || deleteMode || reorderMode;

  const [selectedExam, setSelectedExam] = useState(null);
  const [initialRooms, setInitialRooms] = useState([]);

  const isFinished = record?.isFinished;
  const campaignIsStarted = record?.campaignIsStarted;
  const hasRoomsWithCandidates = record?.rooms?.some(r => r.candidatesCount > 0);
  const canDeleteRoom = campaignIsStarted && !hasRoomsWithCandidates;
  const canReorderRooms = campaignIsStarted && !hasRoomsWithCandidates;

  const checkPassedExams = exams => {
    return exams.some(exam => exam.examPassed === true);
  };

  useEffect(() => {
    if (record && record.exams) {
      setAnyExamPassed(checkPassedExams(record.exams));
    }
  }, [record]);

  const addRoomToExam = (campaignExamId, roomId) =>
    dataProvider.customRequest(candidateRequests.campaignExamRoom(campaignExamId, roomId), null, "POST");

  const deleteRoomToExam = (campaignExamId, roomId) =>
    dataProvider.customRequest(candidateRequests.campaignExamRoom(campaignExamId, roomId), null, "DELETE");

  const reorderRooms = (campaignExamId, reorderedRooms) =>
    dataProvider.customRequest(
      candidateRequests.updateCampaignExamRoomsPriority(campaignExamId),
      { rooms: reorderedRooms },
      "PUT",
    );

  const handleSubmit = async formData => {
    if (!selectedExam) return;
    const campaignExamId = selectedExam.campaignExamId;
    const submittedRooms = formData.rooms ?? [];
    try {
      // ADD MODE
      if (addMode) {
        const roomsToAdd = submittedRooms.filter(r => !selectedExam.rooms.some(er => er.roomId === r.roomId));
        await Promise.all(roomsToAdd.map(room => addRoomToExam(campaignExamId, room.roomId)));
      }
      // DELETE MODE
      if (deleteMode) {
        const roomsToDelete = initialRooms.filter(r => !submittedRooms.some(sr => sr.roomId === r.roomId));
        await Promise.all(roomsToDelete.map(room => deleteRoomToExam(campaignExamId, room.roomId)));
      }
      // REORDER MODE
      if (reorderMode) {
        const reorderedRooms = submittedRooms.map((room, idx) => ({
          roomId: room.roomId,
          order: idx + 1,
        }));
        await reorderRooms(campaignExamId, reorderedRooms);
      }
      notify("Промените са записани успешно", { type: "success" });
      setAddMode(false);
      setDeleteMode(false);
      setReorderMode(false);
      refresh();
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Show
      title="Детайли за предварителна кампания"
      actions={<BackButtonToolbar />}
    >
      <SectionTitle
        title="Информация"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <SimpleShowLayout>
        <Grid
          container
          spacing={3}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Име на български език">
              <NameBgField />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Име на английски език">
              <NameEnField />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Дата на създаване">
              <DateField
                source="createdOn"
                locales="bg-BG"
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Гражданство">
              <CitizenshipFiled isArray />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Начало на регистрация">
              <DateField
                source="registrationFrom"
                locales="bg-BG"
                showTime
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Край на регистрация">
              <DateField
                source="registrationTo"
                locales="bg-BG"
                showTime
              />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Тип">
              <CampaignTypeField subTypeId={CampaignSubTypes.Preliminary} />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            <Labeled label="Направление">
              <DirectionField />
            </Labeled>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
          >
            {isFinished && (
              <Typography
                color={"error"}
                fontWeight={"bold"}
                mb={1}
              >
                Кампанията е завършена
              </Typography>
            )}
            <SecuredComponent permission={permissions.canFinishCampaigns}>
              <FinalizeCampaignButton
                id={record?.id}
                disabled={isFinished}
                campaignName={record?.nameBg}
              />
            </SecuredComponent>
          </Grid>
        </Grid>
      </SimpleShowLayout>

      <SimpleShowLayout>
        <SectionTitle
          title="Специалности"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <Labeled label="Специалност">
              <ReferenceArrayField
                source="specialitiesIds"
                reference={administrationRequests.getSpecialitiesSelect()}
              >
                <SingleFieldList
                  label="Специалност"
                  source="name"
                  linkType={false}
                />
              </ReferenceArrayField>
            </Labeled>
          </Grid>
        </Grid>
      </SimpleShowLayout>
      <SectionTitle
        title="Изпити"
        textAlign="center"
        sx={{ marginTop: "2rem" }}
      />
      <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
        <SimpleShowLayout>
          {anyExamPassed && <ExamStatusSymbols />}
          <ArrayField
            source="exams"
            label=" "
          >
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={settings.style.secondaryDatagrid}
            >
              <ReferenceField
                source="examId"
                label="Изпит"
                reference={administrationRequests.getExamsSelect()}
              >
                <TextField
                  label="Изпит"
                  source="name"
                  sx={{ color: "#4E2683", fontWeight: "bold", fontSize: 16 }}
                />
              </ReferenceField>
              <DateField
                label="Дата на изпит"
                source="examTimestamp"
                locales="bg-BG"
                showTime
              />
              <TextField
                source="enrolledCandidatesCount"
                label="Записани кандидати"
              />
              <CampaignExamButtons isFinished={isFinished} />
              <FunctionField
                label="Зали"
                render={
                  exam =>
                    !exam.examPassed ? (
                      <Accordion sx={{ minWidth: 800 }}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#4E2683" }} />}>
                          <Typography
                            color={"#4E2683"}
                            sx={{ fontWeight: "bold" }}
                          >
                            Зали за изпита:
                          </Typography>
                        </AccordionSummary>{" "}
                        <AccordionDetails sx={{ width: "100%" }}>
                          {!isEditMode && (
                            <ArrayField
                              source="rooms"
                              sortable={false}
                              label="Зали"
                            >
                              <Datagrid
                                bulkActionButtons={false}
                                rowClick={false}
                                sx={settings.style.detailsDatagrid}
                              >
                                <RoomField
                                  source="roomId"
                                  label="Зала"
                                  render={rec => `${rec.roomName} - ${rec.buildingName}`}
                                />
                                <TextField
                                  source="priority"
                                  label="Приоритет"
                                />
                                <TextField
                                  source="candidatesCount"
                                  label="Брой записани"
                                />
                                <TextField
                                  source="capacity"
                                  label="Брой места"
                                />
                                <FunctionField
                                  label=""
                                  render={room => (
                                    <PreviewExamProtocolButton
                                      exam={exam}
                                      slotId={room.slotId}
                                    />
                                  )}
                                />
                              </Datagrid>
                            </ArrayField>
                          )}
                          {campaignIsStarted && !isEditMode && (
                            <SecuredComponent permission={permissions.canUpdateCampaigns}>
                              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                <Button
                                  startIcon={<AddCircleIcon />}
                                  variant="contained"
                                  label="Добави"
                                  sx={{ mt: 1 }}
                                  size="medium"
                                  onClick={() => {
                                    setSelectedExam(exam);
                                    setAddMode(true);
                                  }}
                                />
                                <Button
                                  startIcon={<RemoveCircleIcon />}
                                  variant="contained"
                                  label="Изтрий"
                                  disabled={!canDeleteRoom}
                                  sx={{ mt: 1 }}
                                  size="medium"
                                  onClick={() => {
                                    setSelectedExam(exam);
                                    setInitialRooms(exam.rooms);
                                    setDeleteMode(true);
                                  }}
                                />
                                <Button
                                  startIcon={<ChangeCircleIcon />}
                                  variant="contained"
                                  label="Размени приоритети"
                                  disabled={!canReorderRooms}
                                  sx={{ mt: 1 }}
                                  size="medium"
                                  onClick={() => {
                                    setSelectedExam(exam);
                                    setInitialRooms(exam.rooms);
                                    setReorderMode(true);
                                  }}
                                />
                              </Box>
                            </SecuredComponent>
                          )}
                          {isEditMode && (
                            <SimpleForm
                              key={selectedExam?.campaignExamId}
                              toolbar={false}
                              onSubmit={handleSubmit}
                            >
                              <ArrayInput
                                source="rooms"
                                label=" "
                                defaultValue={selectedExam?.rooms}
                              >
                                <SimpleFormIterator
                                  sx={settings.style.iteratorWithoutGap}
                                  disableAdd={!addMode}
                                  disableReordering={!reorderMode}
                                  disableRemove={!deleteMode}
                                  disableClear
                                >
                                  {reorderMode && <IndexField />}
                                  <ReferenceInput
                                    source="roomId"
                                    reference={administrationRequests.getRoomsSelect()}
                                    filter={{ campaignExamId: selectedExam?.campaignExamId }}
                                  >
                                    <AutocompleteInput
                                      label="Зала"
                                      optionValue="id"
                                      optionText={rec =>
                                        `${rec.roomName} - ${rec.buildingName}${
                                          rec.capacityExam ? ` (${rec.capacityExam})` : ""
                                        }`
                                      }
                                      filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                                      fullWidth
                                    />
                                  </ReferenceInput>
                                </SimpleFormIterator>
                              </ArrayInput>
                              <Box sx={{ textAlign: "left", ml: 2 }}>
                                <Button
                                  label="Откажи"
                                  onClick={() => {
                                    setAddMode(false);
                                    setDeleteMode(false);
                                    setReorderMode(false);
                                  }}
                                  variant="outlined"
                                  color="error"
                                  size="medium"
                                  startIcon={<CloseIcon />}
                                  sx={{ mr: 10 }}
                                />

                                <Button
                                  label="Запази"
                                  type="submit"
                                  color="primary"
                                  variant="contained"
                                  size="medium"
                                  startIcon={<SaveIcon />}
                                />
                              </Box>
                            </SimpleForm>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    ) : null // Hide if examPassed is true
                }
              />
            </Datagrid>
          </ArrayField>
        </SimpleShowLayout>
      </Box>

      <SimpleShowLayout>
        <SectionTitle
          title="Декларации"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Box sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}>
          <ArrayField
            source="declarationDtos"
            label=" "
          >
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={settings.style.secondaryDatagrid}
            >
              <ReferenceField
                source="declarationId"
                label="Декларация"
                reference={documentRequests.getDeclarationsSelect()}
              >
                <TextField
                  label="Декларация"
                  source="name"
                />
              </ReferenceField>
              <CitizenshipFiled label="Гражданство" />
            </Datagrid>
          </ArrayField>
        </Box>
      </SimpleShowLayout>

      <GoogleUsersSection
        campaignId={record?.id}
        exams={record?.exams}
        isFinished={isFinished}
      />

      <SimpleShowLayout>
        <SectionTitle
          title="Ibans"
          textAlign="center"
          sx={{ marginTop: "2rem" }}
        />
        <Grid
          container
          spacing={2}
          sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
        >
          <Grid
            item
            xs={12}
          >
            <Labeled label="Iban">
              <ReferenceArrayField
                source="ibanIds"
                reference={paymentRequests.getIbansSelect()}
              >
                <SingleFieldList
                  label="Iban"
                  source="name"
                />
              </ReferenceArrayField>
            </Labeled>
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </Show>
  );
};

export default ShowCampaign;
